import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';

import { getUserPermissions } from 'src/api/permission-api';
import { usePermissionStore } from 'src/store/permission-store';

export const PERMISSION_QUERY_KEY = ['user-permissions'];

/**
 * 사용자 권한을 가져와 Zustand store에 동기화하는 훅
 * Dashboard 진입 시 호출
 */
export const useInitPermissions = () => {
  const { setPermissions, clearPermissions, setLoading } = usePermissionStore();

  const query = useQuery({
    queryKey: PERMISSION_QUERY_KEY,
    queryFn: getUserPermissions,
    staleTime: Infinity,
    gcTime: 0,
    refetchOnWindowFocus: true,
    retry: 1,
  });

  // 쿼리 결과를 store에 동기화
  useEffect(() => {
    if (query.data) {
      setPermissions(query.data);
    } else if (query.isError) {
      clearPermissions();
    }
  }, [query.data, query.isError, setPermissions, clearPermissions]);

  // 로딩 상태 동기화
  useEffect(() => {
    setLoading(query.isLoading);
  }, [query.isLoading, setLoading]);

  return query;
};

/**
 * 권한 store에서 편집 가능 여부 확인
 * @param menuName 메인 메뉴 이름
 */
export const useCanEditMenu = (menuName: string): boolean =>
  usePermissionStore((state) => state.canEditMenu(menuName));

/**
 * 권한 store에서 조회 가능 여부 확인
 * @param menuName 메인 메뉴 이름
 */
export const useCanViewMenu = (menuName: string): boolean =>
  usePermissionStore((state) => state.canViewMenu(menuName));

/**
 * 권한 store에서 서브메뉴 편집 가능 여부 확인
 * @param menuName 메인 메뉴 이름
 * @param submenuName 서브 메뉴 이름
 */
export const useCanEditSubMenu = (menuName: string, submenuName: string): boolean =>
  usePermissionStore((state) => state.canEditSubMenu(menuName, submenuName));

/**
 * 권한 store에서 서브메뉴 조회 가능 여부 확인
 * @param menuName 메인 메뉴 이름
 * @param submenuName 서브 메뉴 이름
 */
export const useCanViewSubMenu = (menuName: string, submenuName: string): boolean =>
  usePermissionStore((state) => state.canViewSubMenu(menuName, submenuName));
